import { Vector } from "p5";
import { SavedBrushedObject } from "@/types";
import Shape from "../Canvas/SketchUtilities/Shape";

export type BrushModes = "spring" | "pen" | "wipe" | "growshrink" | "shape";

declare global {
  interface Window {
    ReactNativeWebView: any;
    isMobileSplash: boolean;
  }
  interface HTMLCanvasElement {
    captureStream(frameRate?: number): MediaStream;
  }
}

interface baseSketch {
  brushSize: number;
  brushMode: BrushModes;
  isColorfillMode: boolean;
  isColorSelectMode: boolean;
  dispatchColor: (c: string) => void;
  dimensions?: {
    height: number;
    width: number;
  };
}

export interface BackgroundImage {
  image: HTMLImageElement;
  id: number;
}

export interface uiSketchProps extends baseSketch {
  currentColor: string;
  image: number;
  isMobile: boolean;
  isPortrait: boolean;
  images: Dictionary<HTMLImageElement>;
  locationsArr: Vector[];
  shapeInst: Shape;
  shapeType: number;
  shapeMode: ShapeMode;
}

export interface BrushItem {
  name: string;
  description: string;
  gif: string;
  brushType: number;
  minSize: number
}

export interface SideNavItem {
  name: MenuActions;
  clickHandler: () => void;
}

export enum ActiveMenu {
  BRUSH,
  COLOR_SELECT,
  PEN,
  WIPE_TOOL,
  SHAPE,
}

export type MenuActions =
  | "brush"
  | "color"
  | "pen"
  | "spring"
  | "wipe"
  | "shape"
  | "growshrink"
  | "new"
  | "colorPicker"
  | "none";

export interface CanvasResolution {
  name: string;
  description: string;
  aspectRatio: number;
}

export interface GloblaColorData {
  alpha: number;
  darkness: number;
  variations: number;
}

export interface ColorCache {
  data: GloblaColorData;
  color: string[];
  pos?: { x: number; y: number };
}

export interface PenPathPreview {
  path: number;
  url: string | HTMLImageElement;
}

export interface ShapeItem {
  shape: number;
  imageIndex?: string;
  url: string;
  imgElm? : HTMLImageElement; 
}
export type ShapeMode = "rotation" | "drag" | "sliding";
export interface ShapeSetting {
  shape: number;
  shapeSize: number;
  imageIndex?: string;
  shapeRotation: ShapeMode;
}

export interface Dimension {
  width: number;
  height: number;
}

export interface Dictionary<T> {
  [Key: string]: T;
}

export interface History {
  performUndo: boolean;
  performRedo: boolean;
  maxHistoryLength: number;
  historyLength: number;
  historyPos: number;
}

export enum Animation {
  None = -1,
  Wavey_Size = 0,
  Scatter = 1,
  Rotate = 3,
  Wavey_Rotate = 4,
  STROBE = 5,
  ROTATEX = 6,
  ROTATEY = 7,
}
export interface UserSession {
  response_code: number;
  response_desc: string;
  session: string;
  user_id: string;
}

export interface BrushResponse {
  response_code: number;
  response_body: string;
}

export interface BrushState {
  activeBrush: number;
  animation: Animation;
  brushType: BrushModes;
  brushColor: string[];
  cachedColorData?: ColorCache;
  brushIsAnimated: boolean;
  brushSize: number;
  brushCount: number;
  brushSpeed: number;
  isPaletteUsed: boolean;
  colorIsAnimated: boolean;
  colorTransitionSpeed: number;
  growShrink: "grow" | "shrink";
  globalColorData: GloblaColorData;
  penPath: number;
  penRadius: number;
  penSpeed: number;
  shapeSettings: ShapeSetting;
  wipeCount: number;
  wipeSpeed: number;
  wipeSpan: number;
  wipeDirection: "left" | "up" | "right" | "bottom";
}

export interface AppState extends BrushState {
  brushPlaybackRate: number,
  currentActiveMenu: MenuActions;
  CanvasSizeData: CanvasResolution;
  colorBarIsCollapsed: boolean;
  brushbarIsCollapsed: boolean;
  dimensions: Dimension;
  height: number;
  imageModalOpen: boolean;
  isColorfillMode: boolean;
  isColorSelect: boolean;
  width: number;
  drawnOnMobile: boolean;
  images: Dictionary<HTMLImageElement>;
  shapeImages: string[];
  movieAuth?: string;
  userSessionData: {
    session: UserSession | undefined;
    images: string[];
    default_tag: string | null
  };
  userBrushes?: SavedBrushedObject[];
  history: History;
}

export interface SketchProps extends Omit<AppState, "currentActiveMenu"> {
  setLocations: (vec: Vector[]) => void | undefined;
  setGlobalColor: (color: string) => void;
  setHistoryPos: (index: number) => void;
  setColorFromSelect: (color: string) => void;
  shape: Shape;
  isPortrait: boolean;
  stopClickEvents: boolean;
  canvasSize: {
    height: number;
    width: number;
  };

}

export type sessionActions = "set_session" | "clear_sessions" | "set_imageList";

export type actions =
  | "set_movie_auth"
  | "load_Brush"
  | "close_imagemodal"
  | "set_color_speed"
  | "set_colorselect_mode"
  | "set_colorfill_mode"
  | "set_color_animated"
  | "set_palette_used"
  | "set_dimensions"
  | "set_brush_playback_speed"
  | "set_active_brush"
  | "set_active_menu"
  | "set_brush_animated"
  | "set_brush_color"
  | "set_brush_count"
  | "set_brush_size"
  | "set_brush_speed"
  | "set_brush_type"
  | "set_canvas_resolution"
  | "set_colorbar_collapsed"
  | "set_brushbar_collapsed"
  | "set_global_color"
  | "set_image"
  | "set_wipe_speed"
  | "set_wipe_dir"
  | "set_wipe_count"
  | "set_wipe_span"
  | "set_grow_shrink"
  | "set_pen_path"
  | "set_pen_radius"
  | "set_pen_speed"
  | "set_user_brushes"
  | "set_shape"
  | "set_shape_images"
  | "set_history_length"
  | "set_user_settings"
  | "undo"
  | "redo";

export type dispatchAction = {
  type: actions | sessionActions;
  value: any;
};
